import { useCallback, useEffect, useState } from "react";
import { api, money, errMsg, formatDateTime, confirmDelete } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { invalidateEntity } from "@/components/EntitySelect";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const PARTY = [{ name: "name", label: "Ad / Unvan", required: true }, { name: "company_name", label: "Firma Adı" },
  { name: "phone", label: "Telefon" }, { name: "email", label: "E-posta" }, { name: "tax_number", label: "Vergi No" },
  { name: "address", label: "Adres", full: true }];

const TABS = [
  { key: "customers", label: "Müşteriler", fields: [...PARTY, { name: "customer_type", label: "Müşteri Türü", type: "select",
      options: ["Bireysel", "Firma", "İnşaat müşterisi", "Hafriyat müşterisi", "Araç müşterisi", "Diğer"] }, { name: "notes", label: "Not", type: "textarea" }],
    cols: ["name", "company_name", "customer_type", "phone", "tax_number"] },
  { key: "suppliers", label: "Tedarikçiler", fields: [...PARTY, { name: "supplier_type", label: "Tedarikçi Türü" }, { name: "notes", label: "Not", type: "textarea" }],
    cols: ["name", "company_name", "supplier_type", "phone", "tax_number"] },
  { key: "subcontractors", label: "Taşeronlar", fields: [{ name: "name", label: "Ad", required: true }, { name: "company_name", label: "Firma" },
      { name: "phone", label: "Telefon" }, { name: "tax_number", label: "Vergi No" }, { name: "notes", label: "Not", type: "textarea" }],
    cols: ["name", "company_name", "phone", "tax_number"] },
  { key: "employees", label: "Personel", fields: [{ name: "name", label: "Ad Soyad", required: true }, { name: "phone", label: "Telefon" },
      { name: "position", label: "Görev" }, { name: "employee_type", label: "Tür", type: "select", options: ["Personel", "Şoför", "Satış", "Proje", "Yönetici", "Diğer"] },
      { name: "salary", label: "Maaş (₺)", type: "number" }],
    cols: ["name", "employee_type", "position", "phone", "salary"] },
  { key: "equipment", label: "Ekipman", fields: [{ name: "name", label: "Ekipman", required: true }, { name: "equipment_type", label: "Tür" },
      { name: "project_id", label: "Proje", type: "entity", endpoint: "/projects" }, { name: "responsible_employee_id", label: "Sorumlu", type: "entity", endpoint: "/employees" },
      { name: "purchase_date", label: "Alış Tarihi", type: "date" }, { name: "purchase_price", label: "Alış Fiyatı (₺)", type: "number" },
      { name: "current_status", label: "Durum", type: "select", options: ["Aktif", "Bakımda", "Arızalı", "Pasif"] }],
    cols: ["name", "equipment_type", "current_status", "purchase_price"] },
];
const LABELS = { name: "Ad", company_name: "Firma", customer_type: "Tür", supplier_type: "Tür", employee_type: "Tür", phone: "Telefon",
  tax_number: "Vergi No", position: "Görev", salary: "Maaş", equipment_type: "Tür", current_status: "Durum", purchase_price: "Alış Fiyatı" };

export default function Parties() {
  const { can } = useAuth();
  return (
    <div className="space-y-6" data-testid="parties-page">
      <PageHeader title="Cari & Personel" subtitle="Müşteri, tedarikçi, taşeron, personel ve ekipman tanımları — tüm modüller bu kayıtlara bağlanır" />
      <Tabs defaultValue="customers">
        <TabsList className="flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>)}
          {can("kullanici") && <TabsTrigger value="users" data-testid="tab-users">Kullanıcılar</TabsTrigger>}
        </TabsList>
        {TABS.map((t) => <TabsContent key={t.key} value={t.key}><MasterTab cfg={t} /></TabsContent>)}
        {can("kullanici") && <TabsContent value="users"><Users /></TabsContent>}
      </Tabs>
    </div>
  );
}

function MasterTab({ cfg }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const w = can("tanimlar");
  const load = useCallback(() => { api.get(`/${cfg.key}`, { params: { q: q || undefined } }).then((r) => setRows(r.data)); }, [cfg.key, q]);
  useEffect(() => { load(); }, [load]);
  const done = () => { invalidateEntity(`/${cfg.key}`); load(); };
  const archive = async (r) => {
    try { if (await confirmDelete(`/${cfg.key}/${r.id}`)) { toast.success("Kayıt silindi"); done(); } } catch (e) { toast.error(errMsg(e)); }
  };
  const columns = [
    ...cfg.cols.map((c) => ({ key: c, label: LABELS[c], align: ["salary", "purchase_price"].includes(c) ? "right" : undefined,
      render: ["salary", "purchase_price"].includes(c) ? (r) => money(r[c]) : undefined, className: c === "name" ? "font-medium" : "" })),
    { key: "act", label: "", render: (r) => w && (
      <div className="flex gap-1 justify-end">
        <FormDialog title={`${r.name} - Düzenle`} triggerLabel="Düzenle" triggerSize="sm" triggerVariant="ghost" icon={false}
          triggerTestId={`edit-${cfg.key}-${r.id}`} url={`/${cfg.key}/${r.id}`} method="patch" initial={r} fields={cfg.fields}
          transform={(x) => Object.fromEntries(cfg.fields.map((f) => [f.name, x[f.name] ?? null]).filter(([, v]) => v !== null))}
          successMsg="Güncellendi" onDone={done} />
        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => archive(r)} data-testid={`delete-${cfg.key}-${r.id}`}><Trash2 className="w-4 h-4 mr-1" />Sil</Button>
      </div>) },
  ];
  return (
    <Card className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2 justify-between">
        <Input placeholder="Ara..." className="w-64" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${cfg.key}-search`} />
        {w && <FormDialog title={`Yeni - ${cfg.label}`} triggerLabel="Yeni Kayıt" triggerTestId={`add-${cfg.key}-btn`} url={`/${cfg.key}`}
          fields={cfg.fields} initial={{}} successMsg="Kaydedildi" onDone={done} />}
      </div>
      <DataTable columns={columns} rows={rows} testIdPrefix={`${cfg.key}-row`} />
    </Card>
  );
}

function Users() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState({});
  const load = useCallback(() => {
    api.get("/users").then((r) => setRows(r.data));
    api.get("/roles").then((r) => setRoles(Object.fromEntries(r.data.map((x) => [x.id, x.name]))));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-4 space-y-4">
      <div className="flex justify-end">
        <FormDialog title="Yeni Kullanıcı" triggerLabel="Yeni Kullanıcı" triggerTestId="add-user-btn" url="/users" successMsg="Kullanıcı oluşturuldu" onDone={load}
          fields={[{ name: "name", label: "Ad Soyad", required: true }, { name: "email", label: "E-posta", type: "email", required: true },
            { name: "password", label: "Şifre (min 6)", type: "password", required: true },
            { name: "role_id", label: "Rol", type: "entity", endpoint: "/roles", required: true },
            { name: "employee_id", label: "Bağlı Personel", type: "entity", endpoint: "/employees" }]} />
      </div>
      <DataTable testIdPrefix="user-row" rows={rows} columns={[
        { key: "name", label: "Ad", className: "font-medium" }, { key: "email", label: "E-posta" },
        { key: "role_id", label: "Rol", render: (u) => roles[u.role_id] },
        { key: "last_login_at", label: "Son Giriş", render: (u) => formatDateTime(u.last_login_at) }]} />
    </Card>
  );
}
